import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import { ChangeHistory } from "@/common/components/ChangeHistory"
import GoBackButton from "@/common/components/GoBackButton"
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder"
import { ResentContactEmailModal } from "@/common/components/ResentContactEmailModal"
import { useCustomForm } from "@/common/hooks/useCustomForm"
import { useGoBack } from "@/common/hooks/useGoBack"
import { FormTransformers } from "@/common/utils/formTransformers"
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview"
import { ReviewButtons } from "@/features/establishmentLeadsFunnel/components/ReviewButtons"
import { Checklist } from "@/features/privateCompanyLeadsFunnel/components/Checklist"
import { ContractEquipments } from "@/features/privateCompanyLeadsFunnel/components/ContractEquipments"
import { ContractFees } from "@/features/privateCompanyLeadsFunnel/components/ContractFees"
import { ContractInvoiceDetails } from "@/features/privateCompanyLeadsFunnel/components/ContractInvoiceDetails"
import { ContractSignatureDetails } from "@/features/privateCompanyLeadsFunnel/components/ContractSignatureDetails"
import { stateStrategy, translateStateStrategy } from "@/features/privateCompanyLeadsFunnel/constants/stateStrategy.constant"
import useAdvancePrivateCompanyLead from "@/features/privateCompanyLeadsFunnel/hooks/useAdvancePrivateCompanyLead"
import useCreatePrivateCompanyLeadContractFee from "@/features/privateCompanyLeadsFunnel/hooks/useCreatePrivateCompanyLeadContractFee"
import useDeletePrivateCompanyLeadContractFee from "@/features/privateCompanyLeadsFunnel/hooks/useDeletePrivateCompanyLeadContractFee"
import useRegressLead from "@/features/privateCompanyLeadsFunnel/hooks/useRegressLead"
import useReviewContract from "@/features/privateCompanyLeadsFunnel/hooks/useReviewContract"
import useUpdatePrivateCompanyLeadContract from "@/features/privateCompanyLeadsFunnel/hooks/useUpdatePrivateCompanyLeadContract"
import { PrivateCompanyLeadsFunnelUpdateContractValidationSchema, updateContractSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema"
import { PrivateCompanyLeadData } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { updatePrivateCompanyLeadContractDefaultValues } from "@/features/privateCompanyLeadsFunnel/utils/updatePrivateCompanyLeadContractDefaultValues"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { UserRoundIcon } from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import { FormProvider } from "react-hook-form"
import { useParams } from "react-router-dom"

const Form = ({
  privateCompanyLead,
}: {
  privateCompanyLead: PrivateCompanyLeadData,
}) => {
  const { privateCompanyLeadId } = useParams()

  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [serverError, setServerError] = useState(false)

  const contract = useMemo(() => privateCompanyLead.contract, [privateCompanyLead])

  const isInQuotingManagementReview = useMemo(
    () => privateCompanyLead.state === 'quoting_management_review',
    [privateCompanyLead.state]
  )

  const isInQuotingClientReview = useMemo(
    () => privateCompanyLead.state === 'quoting_client_review',
    [privateCompanyLead.state]
  )

  const isInQuotingFinalization = useMemo(
    () => privateCompanyLead.state === 'quoting_finalization',
    [privateCompanyLead.state]
  )

  const isInNegotiating = useMemo(
    () => privateCompanyLead.state === 'negotiating',
    [privateCompanyLead.state]
  )

  const isInCompleted = useMemo(
    () => privateCompanyLead.state === 'completed',
    [privateCompanyLead.state]
  )

  const passedEveryRequirement = useMemo(
    () => privateCompanyLead?.requirement_list_summary?.steps?.every((step) => step.passed),
    [privateCompanyLead]
  )

  const onSuccess = useCallback(() => {
    setServerError(false)
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLead.id] })
    resetDirtyFields();
  }, [setServerError, queryClient, privateCompanyLead.id])

  const onSuccessRedirect = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLead.id] })
    goBack({ fallback: '/private-clients', searchLastRouteFromFallback: true })
  }, [queryClient, privateCompanyLead.id])

  const onError = useCallback(() => setServerError(true), [])

  const {
    mutate: createPrivateCompanyLeadContractFee,
    isPending: createPrivateCompanyLeadContractFeeIsPending
  } = useCreatePrivateCompanyLeadContractFee({
    privateCompanyLeadId,
    onSuccess: () => {
      setValue('contract.contract_fees', null)
      onSuccess()
    },
    onError: () => {
      setValue('contract.contract_fees', null)
      setServerError(true)
    },
  })

  const {
    mutate: deletePrivateCompanyLeadContactFee,
    isPending: deletePrivateCompanyLeadContactFeeIsPending,
  } = useDeletePrivateCompanyLeadContractFee({ privateCompanyLeadId, onSuccess, onError })

  const {
    mutate: advancePrivateCompanyLead,
    isPending: advancePrivateCompanyLeadIsPending,
  } = useAdvancePrivateCompanyLead({ id: privateCompanyLeadId, onSuccess: onSuccessRedirect, onError })

  const {
    mutate: regressLead
  } = useRegressLead({
    id: privateCompanyLeadId,
    onSuccess: onSuccessRedirect,
    onError
  })

  const {
    mutate: approveContract
  } = useReviewContract({
    privateCompanyLeadId,
    state: 'approved',
    onSuccess,
    onError
  })

  const {
    mutate: declineContract
  } = useReviewContract({
    privateCompanyLeadId,
    state: 'disapproved',
    onSuccess,
    onError
  })

  const formMethods = useCustomForm<PrivateCompanyLeadsFunnelUpdateContractValidationSchema>({
    resolver: zodResolver(updateContractSchema),
    defaultValues: updatePrivateCompanyLeadContractDefaultValues(privateCompanyLead.contract),
    shouldOnlyTriggerDirtyFieldsOnSubmit: true,
  })

  const {
    control,
    register,
    setValue,
    handleDirtySubmit,
    resetDirtyFields,
    setError,
    watch,
    formState: { errors }
  } = formMethods;

  const {
    mutate: updateContract,
  } = useUpdatePrivateCompanyLeadContract({
    privateCompanyLead,
    onSuccess,
    onError: FormTransformers.errorsTransformer<PrivateCompanyLeadsFunnelUpdateContractValidationSchema>({ setError }),
  })

  const submit = handleDirtySubmit(updateContract)

  const submittableOnBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>, onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void) => {
      if (e.target.value === '') {
        return
      }
      onBlur(e)
      submit()
    },
    [submit]
  )

  return (
    <FormProvider {...formMethods}>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback={`/private-clients/${privateCompanyLeadId}/edit`} />

          <p className="text-white-400 font-normal">{privateCompanyLead.display_name}</p>
        </div>
        {!isInCompleted && (
          <div className="flex gap-2">
            {(isInQuotingManagementReview || isInNegotiating) && (
              <Button
                type="button"
                disabled={contract?.last_review?.state !== 'disapproved'}
                className={(contract?.last_review?.state === 'disapproved') ? "shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" : ''}
                variant={contract?.last_review?.state === 'disapproved' ? 'yellow-black' : "yellow"}
                onClick={() => regressLead()}
              >
                Solicitar revisão
              </Button>
            )}
            {isInQuotingFinalization && (
              <Button
                type="button"
                className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
                variant="default"
                onClick={() => regressLead()}
                disabled={contract?.last_review?.state !== 'disapproved'}
              >
                Nova condição comercial
              </Button>
            )}
            {
              isInQuotingClientReview && !passedEveryRequirement ? (
                <ResentContactEmailModal contacts={privateCompanyLead.contacts || []} />
              ) :
                <Button
                  type="button"
                  className={passedEveryRequirement ? "shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" : ''}
                  variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                  disabled={!passedEveryRequirement || advancePrivateCompanyLeadIsPending}
                  onClick={() => advancePrivateCompanyLead()}
                  isLoading={advancePrivateCompanyLeadIsPending}
                >
                  {isInNegotiating ? 'Iniciar operação' : isInQuotingFinalization ? 'Enviar para implantação' : (isInQuotingManagementReview || isInQuotingClientReview) ? 'Enviar para Contatos' : 'Enviar para análise do Gestor'}
                </Button>
            }
          </div>
        )}
      </div>
      <div className="flex gap-6 w-full">
        <Box className="flex w-full flex-col gap-4 h-[calc(100vh-128px)] overflow-y-auto shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          {serverError && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>}
          {(isInQuotingManagementReview || isInQuotingFinalization || isInNegotiating) && (
            <div className="flex justify-end">
              <ReviewButtons
                reviewState={contract?.last_review?.state}
                onApprove={approveContract}
                onDecline={declineContract}
              />
            </div>
          )}
          {contract?.last_review?.state === 'disapproved' && (
            <RequestedReview description={contract?.last_review?.description} />
          )}
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/private-clients/${privateCompanyLeadId}/edit`} />
            {(!isInQuotingFinalization && !isInNegotiating && !isInCompleted) ? (
              <div className="flex flex-col">
                <p className="font-bold text-lg">Condição comercial <span className="font-light text-sm italic">{contract?.protocol_number}</span></p>
              </div>
            ) : (
              <div className="flex w-full items-center justify-between gap-2">
                <p className="font-bold text-lg">Contrato</p>
                <div className="flex flex-col items-end">
                  <div className="flex gap-2 items-center">
                    <ImageWithPlaceholder
                      url={privateCompanyLead?.creator?.profile_picture?.url}
                      alt={privateCompanyLead?.creator?.full_name}
                      className="size-6"
                      iconClassName="size-4"
                      placeholderIcon={UserRoundIcon}
                    />
                    <p className="font-bold text-xs">{privateCompanyLead?.creator?.full_name}</p>
                  </div>
                  <p className="font-light text-sm text-blue-800 italic">Condição comercial - {contract?.protocol_number}</p>
                </div>
              </div>
            )}
          </div>
          {(isInQuotingFinalization || isInNegotiating || isInCompleted) && (
            <ContractSignatureDetails
              privateCompanyLead={privateCompanyLead}
              disableFields={isInNegotiating || isInCompleted}
            />
          )}
          <ContractInvoiceDetails
            contract={contract}
            operational_information={privateCompanyLead.operational_information}
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            register={register}
            submit={submit}
            disableFields={isInQuotingManagementReview || isInQuotingClientReview || isInQuotingFinalization || isInNegotiating || isInCompleted}
          />
          <ContractEquipments
            contract={contract}
            operational_information={privateCompanyLead.operational_information}
            control={control}
            errors={errors}
            submittableOnBlur={submittableOnBlur}
            disableFields={isInQuotingManagementReview || isInQuotingClientReview || isInQuotingFinalization || isInNegotiating || isInCompleted}
            setServerError={setServerError}
          />
          <ContractFees
            contract={contract}
            control={control}
            register={register}
            watch={watch}
            errors={errors}
            createPrivateCompanyLeadContractFee={createPrivateCompanyLeadContractFee}
            createPrivateCompanyLeadContractFeeIsPending={createPrivateCompanyLeadContractFeeIsPending}
            deletePrivateCompanyLeadContactFee={deletePrivateCompanyLeadContactFee}
            disableFields={isInQuotingManagementReview || isInQuotingClientReview || isInQuotingFinalization || isInNegotiating || isInCompleted || deletePrivateCompanyLeadContactFeeIsPending}
          />
        </Box>
        <div className="flex flex-col gap-4">
          <Checklist
            requirement_list_summary={privateCompanyLead.requirement_list_summary}
            state={translateStateStrategy[stateStrategy[privateCompanyLead.state as keyof typeof stateStrategy]]}
            className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
            Buttons={() => (
              <>
                {(isInQuotingManagementReview || isInNegotiating) && (
                  <Button
                    type="button"
                    disabled={contract?.last_review?.state !== 'disapproved'}
                    className={(contract?.last_review?.state === 'disapproved') ? "shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" : ''}
                    variant={contract?.last_review?.state === 'disapproved' ? 'yellow-black' : "yellow"}
                    onClick={() => regressLead()}
                  >
                    Solicitar revisão
                  </Button>
                )}
                {isInQuotingFinalization && (
                  <Button
                    type="button"
                    className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
                    variant="default"
                    onClick={() => regressLead()}
                    disabled={contract?.last_review?.state !== 'disapproved'}
                  >
                    Nova condição comercial
                  </Button>
                )}
                {!isInCompleted && (
                  <>
                    {
                      isInQuotingClientReview && !passedEveryRequirement ? (
                        <ResentContactEmailModal contacts={privateCompanyLead.contacts || []} />
                      ) : (
                        <Button
                          type="button"
                          name="advance_lead"
                          variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                          disabled={!passedEveryRequirement || advancePrivateCompanyLeadIsPending}
                          onClick={() => advancePrivateCompanyLead()}
                          isLoading={advancePrivateCompanyLeadIsPending}
                        >
                          {isInNegotiating ? 'Iniciar operação' : isInQuotingFinalization ? 'Enviar para implantação' : (isInQuotingManagementReview || isInQuotingClientReview) ? 'Enviar para Contatos' : 'Enviar para análise do Gestor'}
                        </Button>
                      )
                    }
                  </>
                )}
              </>
            )}
          />
          <ChangeHistory logs={privateCompanyLead?.logs} entityId={privateCompanyLead.id} />
        </div>
      </div>
    </FormProvider>
  )
}

export default Form
